import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import type { DestinationDetail as DestinationDetailType } from '@/data/destination-details/types';
import { useDestinationDetail } from '@/hooks/useDestinationDetail';
import { useDestinationStays } from '@/hooks/useDestinationStays';
import { useDestinationMonthlyIntelligence } from '@/hooks/useDestinationMonthlyIntelligence';
import { getPreviewMonthlyIntelligence } from '@/lib/monthly-intelligence-preview';
import SeasonAbilityGraph from '@/components/destination/SeasonAbilityGraph';
import { useDestinations } from '@/hooks/useDestinations';
import { getFriendlyErrorMessage } from '@/lib/errors';
import { StayCard } from '@/components/destination/StayCard';
import { RecommendationExplanation } from '@/components/RecommendationExplanation';
import type { RecommendationPrefs } from '@/lib/recommendation-reasons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  ArrowLeft,
  Plane,
  Car,
  Waves,
  Users,
  ThermometerSun,
  Utensils,
  Bed,
  Compass,
  HelpCircle,
  MapPin,
  Clock,
  DollarSign,
  Star,
  ChevronRight,
  ChevronDown,
  ImageIcon,
  Shield,
  Smile,
  GraduationCap,
  Mountain,
  Gauge,
  Loader2,
  Heart,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useIsSaved, useToggleSavedDestination } from '@/hooks/useSavedDestinations';
import { toast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import { Seo, normaliseSlug } from '@/components/Seo';
const extractUSD = (costString: string): string => {
  const match = costString.match(/\(~?\$([\d,.–-]+)\s*USD?\)/);
  if (match) return `$${match[1]}`;
  if (costString.startsWith('$')) return costString;
  return costString;
};

const GettingThereIntro = ({ airport, airportCode, distance, name }: { airport: string; airportCode: string; distance: string; name: string; }) => {
  const [expanded, setExpanded] = useState(false);
  const full = `The nearest international airport is ${airport} (${airportCode}), located approximately ${distance} from ${name} depending on traffic and ground transport options available on arrival.`;
  const truncated = full.slice(0, 140);
  const needsTruncate = full.length > 140;
  return (
    <p className="text-foreground/90 leading-relaxed">
      {expanded || !needsTruncate ? full : `${truncated}… `}
      {needsTruncate && (
        <button onClick={() => setExpanded(!expanded)} className="text-primary font-medium hover:underline ml-1">
          {expanded ? 'Show Less' : 'Show More'}
        </button>
      )}
    </p>
  );
};

const DestinationSummary = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const limit = 160;
  const needs = text.length > limit;
  const shown = expanded || !needs ? text : `${text.slice(0, limit).trimEnd()}…`;
  return (
    <div className="bg-secondary/40 border border-border/30 rounded-xl p-3 md:p-4">
      <p className="text-sm md:text-base text-foreground/90 leading-relaxed">
        {shown}{' '}
        {needs && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-primary font-medium hover:underline"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </p>
    </div>
  );
};

const scoreIconFor = (category: string): LucideIcon => {
  const c = category.toLowerCase();
  if (c.includes('swell') || c.includes('wave')) return Waves;
  if (c.includes('water') || c.includes('temp')) return ThermometerSun;
  if (c.includes('crowd')) return Users;
  if (c.includes('safety')) return Shield;
  if (c.includes('budget') || c.includes('cost')) return DollarSign;
  if (c.includes('vibe')) return Smile;
  if (c.includes('lesson')) return GraduationCap;
  if (c.includes('non-surf') || c.includes('activities')) return Mountain;
  if (c.includes('access')) return Gauge;
  return Star;
};

// Some categories are "inverse" — high score = bad (orange)
const isInverseCategory = (category: string): boolean => {
  const c = category.toLowerCase();
  return c.includes('crowd') || c.includes('budget') || c.includes('cost');
};

const barColorFor = (category: string, score: number): string => {
  const inverse = isInverseCategory(category);
  const good = inverse ? score <= 5 : score >= 5;
  return good ? 'bg-primary' : 'bg-destructive';
};

const SCORE_END_LABELS: Record<string, { low: string; high: string }> = {
  'Swell Consistency': { low: 'Inconsistent', high: 'Consistent' },
  'Water Temp': { low: 'Cold', high: 'Warm' },
  'Crowd Level': { low: 'Quiet', high: 'Crowded' },
  'Safety Score': { low: 'Higher risk', high: 'Very safe' },
  'Budget Level': { low: 'Budget', high: 'Expensive' },
};

const SpotScoresCard = ({ scores }: { scores: { category: string; score: number; notes: string }[] }) => {
  return (
    <div className="space-y-2">
      <h3 className="font-serif text-lg md:text-xl text-foreground px-1">Destination Scores</h3>
      <Card className="bg-secondary/40 border-border/30 rounded-xl">
        <CardContent className="space-y-3 md:space-y-4 p-3 md:p-5">
          {scores.map((score) => {
            const Icon = scoreIconFor(score.category);
            const endLabels = SCORE_END_LABELS[score.category];
            return (
              <div key={score.category} className="space-y-1.5">
                <div className="flex justify-between items-center text-sm md:text-base">
                  <span className="text-foreground font-medium flex items-center gap-2">
                    <Icon className="h-4 w-4 text-foreground/70" />
                    {score.category}
                  </span>
                  <span className="text-foreground/80 font-semibold tabular-nums text-xs md:text-sm">
                    {score.score} / 10
                  </span>
                </div>
                <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all duration-700', barColorFor(score.category, score.score))}
                    style={{ width: `${score.score * 10}%` }}
                  />
                </div>
                {endLabels && (
                  <div className="flex justify-between text-[10px] text-muted-foreground/70">
                    <span>{endLabels.low}</span>
                    <span>{endLabels.high}</span>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

const skillColors: Record<string, string> = {
  'Beginner': 'bg-chart-1/20 text-chart-1',
  'Beginner / Intermediate': 'bg-chart-1/20 text-chart-1',
  'Intermediate': 'bg-chart-2/20 text-chart-2',
  'Intermediate / Advanced': 'bg-chart-4/20 text-chart-4',
  'Advanced': 'bg-chart-5/20 text-chart-5',
};

type BreakItem = DestinationDetailType['breaks'][number];

const BreakCard = ({ brk }: { brk: BreakItem }) => {
  const [expanded, setExpanded] = useState(false);
  const accessScore = brk.accessScore ?? (brk.accessDifficulty === 'Hard' ? 8 : brk.accessDifficulty === 'Medium' ? 5 : 2);
  const difficultyScore = brk.difficultyScore ?? (brk.skillLevel.includes('Advanced') && brk.skillLevel.includes('Intermediate')
    ? 7
    : brk.skillLevel.includes('Advanced')
      ? 9
      : brk.skillLevel.includes('Intermediate')
        ? 5
        : 2);

  return (
    <Card className="bg-card border-border/50 overflow-hidden">
      {/* Top Row: Image + Description */}
      <div className="grid md:grid-cols-[420px_1fr] lg:grid-cols-[500px_1fr] gap-0">
        <div className="relative aspect-[16/9] md:aspect-[4/3] md:min-h-[280px] bg-secondary/30 flex items-center justify-center">
          {brk.image ? (
            <img src={brk.image} alt={brk.name} className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground/50">
              <ImageIcon className="h-8 w-8 md:h-12 md:w-12" />
              <span className="text-xs md:text-sm">Photo coming soon</span>
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <CardHeader className="pb-2 md:pb-4 p-3 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2 md:gap-4">
              <CardTitle className="font-serif text-lg md:text-2xl">{brk.name}</CardTitle>
              <Badge className={cn("capitalize text-xs", skillColors[brk.skillLevel] || 'bg-secondary')}>
                {brk.skillLevel}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-3 md:p-6 pt-0 flex-1 space-y-2">
            <p className={cn('text-sm md:text-base text-muted-foreground', !expanded && 'line-clamp-3 md:line-clamp-none')}>
              {brk.description}
            </p>
            {(brk.bottomType || brk.notes) && (
              <div className={cn('flex flex-wrap gap-x-4 gap-y-1 text-xs md:text-sm text-muted-foreground', !expanded && 'hidden md:flex')}>
                {brk.bottomType && (
                  <span><span className="text-foreground/70">Bottom:</span> {brk.bottomType}</span>
                )}
                {brk.notes && (
                  <span><span className="text-foreground/70">Notes:</span> {brk.notes}</span>
                )}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              className="md:hidden text-xs -ml-2 mt-1"
            >
              {expanded ? 'Show less' : 'Show more'}
              <ChevronDown className={cn('ml-1 h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
            </Button>
          </CardContent>
        </div>
      </div>

      {/* Bottom Row: Stats & Scores */}
      <div className={cn('border-t border-border/50 p-3 md:p-6 bg-secondary/20 space-y-4', !expanded && 'hidden md:block')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          <div className="p-2 md:p-3 bg-background/50 rounded-lg">
            <div className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Wave Type</div>
            <div className="font-medium text-xs md:text-base">{brk.waveType}</div>
          </div>
          <div className="p-2 md:p-3 bg-background/50 rounded-lg">
            <div className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Direction</div>
            <div className="font-medium text-xs md:text-base">{brk.direction}</div>
          </div>
          <div className="p-2 md:p-3 bg-background/50 rounded-lg">
            <div className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Swell Range</div>
            <div className="font-medium text-xs md:text-base">{brk.swellRange}</div>
          </div>
          <div className="p-2 md:p-3 bg-background/50 rounded-lg">
            <div className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Ideal Tide</div>
            <div className="font-medium text-xs md:text-base">{brk.idealTide}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <div className="space-y-1 md:space-y-2">
            <div className="flex justify-between text-[10px] md:text-sm">
              <span className="text-muted-foreground flex items-center gap-1 md:gap-2">
                <Users className="h-3 w-3 md:h-4 md:w-4" />
                <span>Crowd</span>
              </span>
              <span className="font-medium">{brk.crowdFactor}/10</span>
            </div>
            <div className="h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${brk.crowdFactor > 6 ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${brk.crowdFactor * 10}%` }}
              />
            </div>
          </div>
          <div className="space-y-1 md:space-y-2">
            <div className="flex justify-between text-[10px] md:text-sm">
              <span className="text-muted-foreground flex items-center gap-1 md:gap-2">
                <Star className="h-3 w-3 md:h-4 md:w-4" />
                <span>Consistency</span>
              </span>
              <span className="font-medium">{brk.consistencyRating}/10</span>
            </div>
            <div className="h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${brk.consistencyRating < 5 ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${brk.consistencyRating * 10}%` }}
              />
            </div>
          </div>
          <div className="space-y-1 md:space-y-2">
            <div className="flex justify-between text-[10px] md:text-sm">
              <span className="text-muted-foreground flex items-center gap-1 md:gap-2">
                <MapPin className="h-3 w-3 md:h-4 md:w-4" />
                <span>Access</span>
              </span>
              <span className="font-medium">{accessScore}/10</span>
            </div>
            <div className="h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${accessScore < 5 ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${accessScore * 10}%` }}
              />
            </div>
          </div>
          <div className="space-y-1 md:space-y-2">
            <div className="flex justify-between text-[10px] md:text-sm">
              <span className="text-muted-foreground flex items-center gap-1 md:gap-2">
                <Waves className="h-3 w-3 md:h-4 md:w-4" />
                <span>Difficulty</span>
              </span>
              <span className="font-medium">{difficultyScore}/10</span>
            </div>
            <div className="h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${difficultyScore < 5 ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${difficultyScore * 10}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const SurfCultureCard = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="font-serif text-xl">Surf Culture</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className={cn('text-muted-foreground', !expanded && 'line-clamp-4 md:line-clamp-none')}>{text}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          className="md:hidden text-xs -ml-2"
        >
          {expanded ? 'Show less' : 'Show more'}
          <ChevronDown className={cn('ml-1 h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
        </Button>
      </CardContent>
    </Card>
  );
};

const DestinationDetail = () => {
  const { destinationId } = useParams<{ destinationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const incomingPreferences = (location.state as { preferences?: RecommendationPrefs } | null)?.preferences;

  const { data: destination, isLoading, error } = useDestinationDetail(destinationId);
  const { data: allDestinations = [] } = useDestinations();
  const uiDestination = allDestinations.find((d) => d.id === destinationId);
  const { user } = useAuth();
  const dbId = destination?.dbId;
  const { data: isSaved } = useIsSaved(dbId);
  const { data: stays = [] } = useDestinationStays(dbId);
  // Explicit dev/preview fixture, never a fallback: when the flag is off (all
  // production builds) this is null and the real hook's data is used verbatim.
  const previewMonthlyData = getPreviewMonthlyIntelligence(destinationId);
  const { data: liveMonthlyData = [], isLoading: monthlyLoading } = useDestinationMonthlyIntelligence(
    previewMonthlyData ? undefined : dbId,
  );
  const monthlyData = previewMonthlyData ?? liveMonthlyData;
  const toggleSaved = useToggleSavedDestination();

  useEffect(() => {
    if (destination?.name) {
      trackEvent('destination_opened', {
        destination_name: destination.name,
        destination_slug: destinationId,
        current_page: window.location.pathname,
      });
    }
  }, [destination?.name, destinationId]);

  // Monthly intelligence is additive -- most destinations will eventually have it,
  // but the V1 dataset covers 55 of the current catalogue, not all of them. An
  // empty result is expected and handled gracefully by SeasonAbilityGraph; this is
  // purely a dev-console signal so a missing row doesn't go unnoticed while the
  // dataset is still rolling out, without ever surfacing to real users.
  useEffect(() => {
    if (import.meta.env.DEV && destination?.name && !monthlyLoading && monthlyData.length === 0) {
      console.warn(
        `[destination-monthly-intelligence] No monthly rows for "${destination.name}" (dbId: ${dbId}). ` +
          'Expected if this destination is outside the V1 research set; verify against the destinations catalogue if not.'
      );
    }
  }, [destination?.name, dbId, monthlyLoading, monthlyData.length]);

  const handleToggleSave = () => {
    if (!user) {
      toast({ title: 'Sign in required', description: 'Create an account to save destinations.' });
      navigate('/auth');
      return;
    }
    if (!dbId) return;
    toggleSaved.mutate({ destinationId: dbId, saved: !!isSaved });
  };

  if (isLoading) {
    return (
      <Layout>
        {/* min-h-screen: without a real content height here, the footer
            renders immediately below this tiny spinner block, then jumps
            far down the page once the real (much taller) content loads --
            measured at CLS 0.55-0.67 on this page specifically, the
            largest single contributor to layout shift anywhere in the app.
            Reserving roughly a full viewport keeps that jump off-screen. */}
        <div className="container mx-auto px-4 py-20 min-h-screen flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading destination…</p>
        </div>
      </Layout>
    );
  }

  if (error || !destination) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="font-serif text-3xl mb-4">Destination Not Found</h1>
          <p className="text-muted-foreground mb-8">
            {error ? getFriendlyErrorMessage(error) : "We haven't added detailed information for this destination yet."}
          </p>
          <Button onClick={() => navigate('/destinations')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Destinations
          </Button>
        </div>
      </Layout>
    );
  }



  const canonicalSlug = normaliseSlug(destination.id ?? destinationId) || normaliseSlug(destination.name);
  const canonicalUrl = `https://www.swellbreaks.com/destinations/${canonicalSlug}`;
  const hasRealHeroImage = !!destination.heroImage && !destination.heroImage.includes('images.unsplash.com/photo-1502680390469-be75c86b636f');

  const placeSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    name: destination.name,
    url: canonicalUrl,
    ...(destination.description ? { description: destination.description } : {}),
    ...(destination.country ? { touristType: 'Surfers', address: { '@type': 'PostalAddress', addressCountry: destination.country } } : {}),
    ...(hasRealHeroImage ? { image: destination.heroImage } : {}),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.swellbreaks.com/' },
      { '@type': 'ListItem', position: 2, name: 'Destinations', item: 'https://www.swellbreaks.com/destinations' },
      { '@type': 'ListItem', position: 3, name: destination.name, item: canonicalUrl },
    ],
  };

  return (
    <Layout>
      <Seo
        title={`${destination.name} Surf Guide | Best Months, Ability and Where to Stay`}
        description={`Plan a surf trip to ${destination.name}. Compare the best months, suitable surf levels, wave types, crowds, costs and recommended places to stay.`}
        path={`/destinations/${canonicalSlug}`}
        jsonLd={[placeSchema, breadcrumbSchema]}
      />
      {/* Hero Section - Compact on desktop. The photograph is meaningful product
          content (not decorative background), so it stays as unobstructed as
          possible: no image-wide overlay, only a short gradient confined to the
          bottom band behind the title/location, plus a restrained text shadow
          for insurance. */}
      <section className="relative h-[45vh] md:h-[40vh] lg:h-[35vh] min-h-[300px] md:min-h-[280px] flex items-end">
        <div className="absolute inset-0">
          <img
            src={destination.heroImage}
            alt={destination.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-background/95 via-background/50 to-transparent" />
        </div>
        <div className="container mx-auto px-4 pb-4 md:pb-6 relative z-10">
          <Button
            variant="ghost"
            size="sm"
            className="mb-2 md:mb-4 text-foreground/80 hover:text-foreground rounded-full text-xs md:text-sm drop-shadow-sm"
            onClick={() => navigate('/destinations')}
          >
            <ArrowLeft className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2 mb-1 md:mb-2">
            <MapPin className="h-4 w-4 text-primary drop-shadow-sm" />
            <span className="text-foreground/70 text-sm md:text-base drop-shadow-sm">{destination.country}</span>
          </div>
          <div className="flex items-start justify-between gap-4">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-semibold text-foreground drop-shadow-sm">
              {destination.name}
            </h1>
            <Button
              variant={isSaved ? 'default' : 'outline'}
              size="sm"
              onClick={handleToggleSave}
              disabled={toggleSaved.isPending}
              className="rounded-full gap-2 shrink-0"
            >
              <Heart className={cn('h-4 w-4', isSaved && 'fill-current')} />
              {isSaved ? 'Saved' : 'Save'}
            </Button>
          </div>
        </div>
      </section>



      {/* Main Content - Compact on desktop */}
      <section className="relative -mt-4 md:-mt-4">
        <div className="container mx-auto px-3 md:px-4">
          <div className="bg-card rounded-2xl border border-foreground/20 p-4 sm:p-5 md:p-6 lg:p-8 shadow-xl">
            {/* Fit block: personalised match (if arriving from planner) + description + scores */}
            <section id="fit" className="space-y-4 md:space-y-6 mb-6 md:mb-8">
              {incomingPreferences && uiDestination && (
                <div className="space-y-2">
                  <p className="text-sm md:text-base text-foreground/85">
                    Why we matched you with {destination.name}:
                  </p>
                  <RecommendationExplanation
                    destination={uiDestination}
                    preferences={incomingPreferences}
                    monthlyRows={monthlyData}
                  />
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 md:gap-6 items-start">
                <div>
                  <DestinationSummary text={destination.description} />
                </div>
                {destination.spotScores && destination.spotScores.length > 0 && (
                  <SpotScoresCard scores={destination.spotScores} />
                )}
              </div>
            </section>

            {/* When should you go? — only when we have monthly intelligence.
                Sits deliberately between "why does it suit me" (#fit, above)
                and "what waves can I surf" (#waves, below): what is it -> why
                does it suit me -> when should I go -> what waves are there. */}
            {monthlyData.length > 0 && (
              <section id="season-ability" className="space-y-4 md:space-y-6 mb-8 md:mb-12">
                <SeasonAbilityGraph monthlyData={monthlyData} />
              </section>
            )}

            {/* The surf */}
            <section id="waves" className="space-y-4 md:space-y-6 mb-8 md:mb-12">
              <div className="space-y-1">
                <h2 className="font-serif text-2xl md:text-3xl text-foreground flex items-center gap-2">
                  <Waves className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                  The surf
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  The breaks that define {destination.name}, and how they surf.
                </p>
              </div>

              <div className="grid gap-6 md:gap-8">
                {destination.breaks.map((brk) => (
                  <BreakCard key={brk.name} brk={brk} />
                ))}
              </div>

              {/* Surf Culture */}
              <SurfCultureCard text={destination.surfInfo.description} />
            </section>

            {/* Where you'd stay — only when we have stays */}
            {stays.length > 0 && (
              <section id="stay" className="space-y-4 mb-8 md:mb-12">
                <div className="space-y-1">
                  <h2 className="font-serif text-2xl md:text-3xl text-foreground flex items-center gap-2">
                    <Bed className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                    Where you'd stay
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground">
                    {stays.length} hand-picked {stays.length === 1 ? 'option' : 'options'} — walkable to the waves, honest about what they offer.
                  </p>
                </div>

                <div className="grid gap-4 md:gap-5">
                  {stays.map((stay) => (
                    <StayCard key={stay.id} stay={stay} destinationName={destination.name} />
                  ))}
                </div>
              </section>
            )}

            {/* Getting there */}
            <section id="getting-there" className="space-y-4 mb-8 md:mb-12">
              <div className="space-y-1">
                <h2 className="font-serif text-2xl md:text-3xl text-foreground flex items-center gap-2">
                  <Plane className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                  Getting there
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  Rough flight costs, and how to get from the airport to the coast.
                </p>
              </div>

              <Card className="bg-card border-border/50">
                <CardContent className="p-4 space-y-4">
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-muted-foreground">Estimated return flights</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {[
                        { region: 'Europe', cost: extractUSD(destination.gettingThere.flightCosts.europe), time: '16–20 hrs' },
                        { region: 'North America', cost: extractUSD(destination.gettingThere.flightCosts.usa), time: '18–24 hrs' },
                        { region: 'Australia', cost: extractUSD(destination.gettingThere.flightCosts.australia), time: '6–8 hrs' },
                      ].map((row) => (
                        <div
                          key={row.region}
                          className="flex items-center justify-between bg-secondary/40 rounded-lg p-3"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground uppercase tracking-wide w-20">{row.region}</span>
                            <span className="text-sm text-foreground/70 flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5 text-primary/70" />
                              {row.time}
                            </span>
                          </div>
                          <span className="font-semibold text-foreground text-sm">{row.cost}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border/50">
                <CardHeader className="pb-2 px-4 pt-4">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Car className="h-4 w-4 text-primary" />
                    From the airport
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-4">
                  <GettingThereIntro
                    airport={destination.gettingThere.airport}
                    airportCode={destination.gettingThere.airportCode}
                    distance={destination.gettingThere.distanceFromAirport}
                    name={destination.name}
                  />
                  <div className="space-y-2">
                    {destination.gettingThere.transportOptions.map((t) => (
                      <div
                        key={t.option}
                        className="flex items-center justify-between bg-secondary/30 rounded-lg p-3"
                      >
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium text-sm">{t.option}</span>
                          <span className="text-xs text-muted-foreground">{t.time} · {t.notes}</span>
                        </div>
                        <span className="font-semibold text-sm text-primary whitespace-nowrap ml-2">{t.priceUSD}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Between surfs */}
            <section id="activities" className="space-y-4 mb-8 md:mb-12">
              <div className="space-y-1">
                <h2 className="font-serif text-2xl md:text-3xl text-foreground flex items-center gap-2">
                  <Compass className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                  Between surfs
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  When the surf is flat or you want a break from the water — what else fills the day here.
                </p>
              </div>

              <Card className="bg-card border-border/50">
                <CardContent className="space-y-8 p-4 md:p-6">
                  {destination.activitiesBlog ? (
                    <div className="space-y-8">
                      <p className="text-lg text-foreground leading-relaxed">
                        {destination.activitiesBlog.intro}
                      </p>

                      {destination.activitiesBlog.sections.map((section, index) => (
                        <div
                          key={section.title}
                          className={cn(
                            "grid gap-6",
                            section.image ? "md:grid-cols-2" : "md:grid-cols-1",
                            index % 2 === 1 && section.image && "md:[&>*:first-child]:order-2"
                          )}
                        >
                          <div className="flex flex-col justify-center space-y-3">
                            <h3 className="font-serif text-xl text-foreground">{section.title}</h3>
                            <p className="text-muted-foreground leading-relaxed">{section.content}</p>
                          </div>
                          {section.image && (
                            <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                              <img
                                src={section.image}
                                alt={section.title}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            </div>
                          )}
                        </div>
                      ))}

                      {destination.activitiesBlog.quote && (
                        <blockquote className="border-l-4 border-primary pl-6 py-4 my-8 bg-primary/5 rounded-r-lg">
                          <p className="text-lg italic text-foreground mb-2">
                            "{destination.activitiesBlog.quote.text}"
                          </p>
                          <cite className="text-sm text-muted-foreground">
                            — {destination.activitiesBlog.quote.source}
                          </cite>
                        </blockquote>
                      )}
                    </div>
                  ) : (
                    <ul className="grid md:grid-cols-2 gap-3">
                      {destination.otherActivities.map((activity) => (
                        <li key={activity} className="flex items-start gap-3">
                          <ChevronRight className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{activity}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* Still have questions */}
            <section id="faq" className="space-y-4">
              <div className="space-y-1">
                <h2 className="font-serif text-2xl md:text-3xl text-foreground flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                  Still have questions
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  The things people usually ask before booking a trip to {destination.name}.
                </p>
              </div>

              <Card className="bg-card border-border/50">
                <CardContent className="p-4 md:p-6">
                  <Accordion type="single" collapsible className="w-full">
                    {destination.faqs.map((faq, index) => (
                      <AccordionItem key={index} value={`faq-${index}`}>
                        <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </section>

          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-24" />
    </Layout>
  );
};

export default DestinationDetail;
